import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import Imgix from 'react-imgix';
import { useWindowSize } from 'usehooks-ts';

import proTag from '../../../../assets/images/pro_tag.svg';

import { article_path, briefing_path } from '../../../routes';
import { WIDTH_MD, WIDTH_SM } from '../../../utils/constants';
import { getArticleImgSrc } from '../../../utils/images';
import { MaybeLink } from '../../BaseUI';
import AuthorsLine, { ArticleAuthors } from '../AuthorsLine';
import EntityTopics, { EntityTopic } from '../EntityTopics';
import Highlight from '../Highlight';
import styles from './styles.module.scss';

export type ArticleOrBriefing = {
  authors: ArticleAuthors;
  commentCount: number;
  createdAt: string;
  dek?: string;
  entityTopics: EntityTopic[];
  excerpt: { short: string; long: string };
  guid: string;
  headline?: string;
  highlight: string;
  highlightSlug?: string;
  id: number;
  isPro?: boolean;
  picture: string | null;
  publishOnWeb: boolean;
  publishedAt?: string;
  slug: string;
  title?: string;
  type: 'Article' | 'Briefing';
};

interface ArticleOrBriefingFeedItemProps {
  articleOrBriefing: ArticleOrBriefing;
  useLargeImage?: boolean;
  useFeaturedLayout?: boolean;
}

export default function ArticleOrBriefingFeedItem({
  articleOrBriefing,
  useLargeImage,
  useFeaturedLayout,
}: ArticleOrBriefingFeedItemProps) {
  const [imgWidth, setImgWidth] = useState(0);
  const { width } = useWindowSize();

  const showFeaturedLayout = useFeaturedLayout && width < WIDTH_SM;

  useEffect(() => {
    // only show featured image on smaller screens, and set to screen size
    if (showFeaturedLayout) {
      setImgWidth(width);
    } else if (useLargeImage) {
      // standard img is bigger on pages like the search page
      setImgWidth(355);
    } else {
      // standard img is smaller on pages like directory page
      setImgWidth(270);
    }
  }, [showFeaturedLayout, useLargeImage, width]);

  const type = articleOrBriefing.type;
  const articlePath = articleOrBriefing.publishOnWeb
    ? article_path(articleOrBriefing.slug)
    : undefined;

  const href = type === 'Article' ? articlePath : briefing_path(articleOrBriefing.guid);
  return (
    <div className={clsx(styles.article, { [styles.featured]: showFeaturedLayout })}>
      <div
        className={clsx(styles.feedItem, {
          [styles.featured]: showFeaturedLayout,
        })}
        data-test="article-or-briefing-feed-item"
      >
        <div className={styles.content}>
          <div className={styles.details}>
            {!!articleOrBriefing.highlight && articleOrBriefing.highlightSlug && (
              <Highlight
                name={articleOrBriefing.highlight}
                slug={articleOrBriefing.highlightSlug}
              />
            )}
            {!!articleOrBriefing.entityTopics && (
              <EntityTopics topics={articleOrBriefing.entityTopics} />
            )}
          </div>
          <MaybeLink
            href={href}
            className="track-link"
            id={`article-${articleOrBriefing.id}`}
            data-type="link"
            data-name="article title"
            data-location="feed"
          >
            <div className={styles.title}>
              {articleOrBriefing.title || articleOrBriefing.headline}
            </div>
          </MaybeLink>
          <AuthorsLine
            authors={articleOrBriefing.authors}
            date={articleOrBriefing.publishedAt || articleOrBriefing.createdAt}
            commentCount={articleOrBriefing.commentCount}
            slug={articleOrBriefing.slug}
          />
          {width >= WIDTH_SM && (
            <div className={styles.excerpt}>
              <MaybeLink href={href}>
                {ReactHtmlParser(
                  width >= WIDTH_MD
                    ? articleOrBriefing.excerpt.long
                    : articleOrBriefing.excerpt.short
                )}
              </MaybeLink>
            </div>
          )}
        </div>
        <div
          className={clsx(styles.image, {
            [styles.imageLarge]: useLargeImage,
          })}
        >
          <MaybeLink
            href={href}
            className="track-link relative"
            id={`article-image-${articleOrBriefing.id}`}
            data-type="link"
            data-name="article image"
            data-location="feed"
          >
            <Imgix
              src={getArticleImgSrc(articleOrBriefing.id, articleOrBriefing.picture)}
              width={imgWidth}
              height={(imgWidth * 9) / 16}
              imgixParams={{ fit: 'crop', 'gif-q': '50' }}
              className="article-image xs-d:max-w-full"
            />
            {articleOrBriefing.isPro && <img src={proTag} alt="Pro" className="pro-tag" />}
          </MaybeLink>
        </div>
      </div>
    </div>
  );
}
